import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { colors } from '../Colors';
import { galleryImageApi } from '../services/api';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CardMedia,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  Image as ImageIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';

function ImagesGaleryScreen() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newImageFile, setNewImageFile] = useState(null);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageName, setNewImageName] = useState('');
  const [uploading, setUploading] = useState(false);

  // Fetch images on component mount
  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await galleryImageApi.getAllImages();
      if (response.success && response.data && response.data.images) {
        setImages(response.data.images);
      } else {
        setError('Erreur lors du chargement des images');
        setImages([]);
      }
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Erreur lors du chargement des images: ' + err.message);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    try {
      setError(null);
      await galleryImageApi.deleteImage(id);
      // Refresh images list
      await fetchImages();
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Erreur lors de la suppression de l\'image: ' + err.message);
    }
  };

  const handleOpenAddDialog = () => {
    setNewImageFile(null);
    setNewImageUrl('');
    setNewImageName('');
    setError(null);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setNewImageFile(null);
    setNewImageUrl('');
    setNewImageName('');
    setError(null);
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove the data URL prefix (data:image/...;base64,) and keep only the base64 string
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleAddImage = async () => {
    if (!newImageFile && !newImageUrl.trim()) {
      setError('Veuillez sélectionner un fichier ou entrer une URL');
      return;
    }

    if (newImageFile && newImageUrl.trim()) {
      setError('Veuillez choisir soit un fichier, soit une URL, pas les deux');
      return;
    }

    // Validate file type if file is selected
    if (newImageFile) {
      const validMimeTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
      const fileName = newImageFile.name.toLowerCase();
      const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!validMimeTypes.includes(newImageFile.type) && !hasValidExtension) {
        setError('Veuillez sélectionner un fichier image valide (JPG, PNG, GIF, WEBP)');
        return;
      }
    }

    try {
      setUploading(true);
      setError(null);
      
      let fileBase64 = null;
      if (newImageFile) {
        // Convert file to base64
        fileBase64 = await convertFileToBase64(newImageFile);
      }
      
      // Upload to API
      await galleryImageApi.uploadImage(
        newImageName.trim() || 'Nouvelle Image',
        fileBase64,
        newImageUrl.trim() || null
      );
      
      // Refresh images list
      await fetchImages();
      handleCloseAddDialog();
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Erreur lors de l\'upload de l\'image: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background.primary }}>
      <Navigation />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ImageIcon sx={{ fontSize: '2.5rem', color: colors.primary.main }} />
            <Typography variant="h4" sx={{ color: colors.text.primary, fontWeight: 'bold' }}>
              Galerie d'Images
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            disabled={loading}
            sx={{
              bgcolor: colors.primary.main,
              borderRadius: '20px',
              px: 3,
              py: 1,
              fontSize: '1rem',
              fontWeight: 'bold',
              '&:hover': { 
                bgcolor: colors.primary.dark,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Ajouter une image
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} sx={{ color: colors.primary.main }} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : images.length === 0 ? (
          <Card sx={{ borderRadius: 4, bgcolor: colors.background.secondary, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)' }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <ImageIcon sx={{ fontSize: '4rem', color: colors.text.light, mb: 2 }} />
              <Typography variant="h4" sx={{ mb: 2, color: colors.text.primary, fontWeight: 'bold' }}>
                Aucune image
              </Typography>
              <Typography variant="h6" sx={{ color: colors.text.secondary }}>
                Aucune image disponible pour le moment
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {images.map((image) => (
              <Grid item xs={12} sm={6} md={4} key={image.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    bgcolor: colors.background.secondary,
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                    },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardMedia
                    component="img"
                    image={image.url}
                    alt={image.name || 'Image'}
                    sx={{
                      width: '100%',
                      height: 250,
                      objectFit: 'cover'
                    }}
                  />
                  <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          color: colors.text.primary,
                          fontWeight: 'bold',
                          flex: 1
                        }}
                      >
                        {image.name || 'Sans titre'}
                      </Typography>
                      <IconButton
                        onClick={() => handleDeleteImage(image.id)}
                        disabled={loading}
                        sx={{
                          color: '#E74C3C',
                          '&:hover': {
                            bgcolor: 'rgba(231, 76, 60, 0.1)',
                            transform: 'scale(1.1)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Add Image Dialog */}
        <Dialog
          open={addDialogOpen}
          onClose={handleCloseAddDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '20px',
              border: '2px solid ' + colors.primary.main
            }
          }}
        >
          <DialogTitle sx={{
            pb: 2,
            background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
            color: 'white',
            borderRadius: '20px 20px 0 0'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Ajouter une image
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Nom de l'image"
              value={newImageName}
              onChange={(e) => setNewImageName(e.target.value)}
              sx={{ mb: 3 }}
              placeholder="Nom de l'image"
            />

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Option 1: Importer depuis un fichier
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  setNewImageFile(e.target.files[0] || null);
                  if (e.target.files[0]) {
                    setNewImageUrl(''); // Clear URL if file is selected
                  }
                }}
                style={{ width: '100%' }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Option 2: Importer depuis une URL
              </Typography>
              <TextField
                fullWidth
                label="URL de l'image"
                value={newImageUrl}
                onChange={(e) => {
                  setNewImageUrl(e.target.value);
                  if (e.target.value.trim()) {
                    setNewImageFile(null); // Clear file if URL is entered
                  }
                }}
                placeholder="https://example.com/image.jpg"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button
              onClick={handleCloseAddDialog}
              disabled={uploading}
              sx={{ borderRadius: '20px', textTransform: 'none' }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleAddImage}
              variant="contained"
              disabled={uploading || (!newImageFile && !newImageUrl.trim())}
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              sx={{
                bgcolor: colors.primary.main,
                borderRadius: '20px',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: colors.primary.dark
                }
              }}
            >
              {uploading ? 'Upload...' : 'Ajouter'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}

export default ImagesGaleryScreen;

